import { useState } from "react"
import { useLoading } from "../../context/LoadingContext"
import { toastError, toastSuccess } from "../../utils/toast"
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, TableProps } from "antd"
import { LeftCircleOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons"
import { formatCurrency } from "../../utils/string-utils"
import { useNavigate } from "react-router-dom"
import {apiAddMetaDataPackageDataSponsor, apiAddPackageDataSponsors} from "../../api"
import { PACKAGES } from "../../assets/constants/route_path"
import {
    groupCodeOptions,
    partnerPackageDataSponsorSourceOptions,
    providerSourceOptions, serviceCodeOptions,
    TDM_SOURCE
} from "../../assets/constants/default_option"
import {serviceCode} from "../../assets/constants/constants";

interface ProviderMetadata {
    provider: string | null;
    serviceCode: string;
    price: number;
    discountAmount: number;
    netPrice: number;
    groupCode: string;
}

const DEFAULT_PROVIDER_METADATA: ProviderMetadata = {
    provider: null,
    serviceCode: serviceCode.VDS_VIETTEL_KEY,
    price: 0,
    discountAmount: 0,
    netPrice: 0,
    groupCode: '3'
}

export default function PackageCreatePackages() {
    const { setIsLoading } = useLoading()
    const [listAddPackages, setListAddPackages] = useState<any>([])
    const navigate = useNavigate()
    const [formCreate] = Form.useForm()
    const [isOpenModal, setIsOpenModal] = useState(false)
    const [otpConfirm, setOtpConfirm] = useState<string>('')
    const [providerMetadata, setProviderMetadata] = useState<ProviderMetadata[]>([{...DEFAULT_PROVIDER_METADATA}])
    const [dataModalProvider, setDataModalProvider] = useState<any>(null)

    const handleAddProviderField = () => {
        if (providerMetadata.length >= providerSourceOptions.length) {
            toastError(`Số lượng nhà cung cấp tối đa là ${providerSourceOptions.length}!`)
            return
        }
        const priceUnit = dataModalProvider?.priceUnit || 0
        setProviderMetadata([...providerMetadata, {...DEFAULT_PROVIDER_METADATA, price: priceUnit, netPrice: priceUnit }])
    }

    const handleRemoveProviderField = (index: number) => {
        if (providerMetadata.length === 1) {
            toastError('Phải có ít nhất một nhà cung cấp!')
            return
        }
        const newProvider = providerMetadata.filter((_, i) => i !== index)
        setProviderMetadata(newProvider)
    }

    const handleProviderChange = (index: number, field: keyof ProviderMetadata, value: any) => {
        const newProvider = [...providerMetadata]
        newProvider[index] = { ...newProvider[index], [field]: value }

        // Auto-calculate netPrice when discountAmount changes
        if (field === 'discountAmount') {
            const priceUnit = dataModalProvider?.priceUnit || 0
            newProvider[index].netPrice = priceUnit - (value || 0)
        }

        setProviderMetadata(newProvider)
    }

    const handleAddProviderMetadata = () => {
        if (!dataModalProvider) {
            toastError('Không tìm thấy thông tin gói cước!')
            return
        }

        // Validate at least one provider is filled
        const validProviders = providerMetadata.filter(p => p.provider)
        if (validProviders.length === 0) {
            toastError('Vui lòng nhập ít nhất một nhà cung cấp hợp lệ!')
            return
        }

        // Validate all required fields for each provider
        for (let i = 0; i < validProviders.length; i++) {
            const provider = validProviders[i]

            if (!provider.serviceCode || provider.serviceCode.trim() === '') {
                toastError(`Nhà cung cấp ${i + 1}: Vui lòng chọn dịch vụ!`)
                return
            }

            if (provider.price === undefined || provider.price === null || provider.price <= 0) {
                toastError(`Nhà cung cấp ${i + 1}: Vui lòng nhập giá gốc hợp lệ!`)
                return
            }

            if (provider.discountAmount === undefined || provider.discountAmount === null || provider.discountAmount < 0) {
                toastError(`Nhà cung cấp ${i + 1}: Vui lòng nhập giá chiết khấu hợp lệ!`)
                return
            }

            if (!provider.groupCode || provider.groupCode.trim() === '') {
                toastError(`Nhà cung cấp ${i + 1}: Vui lòng chọn nhóm!`)
                return
            }
        }

        // Check for duplicate providers
        const providerNames = validProviders.map(p => p.provider)
        const uniqueProviders = new Set(providerNames)
        if (providerNames.length !== uniqueProviders.size) {
            toastError('Không chọn trùng nhà cung cấp cho một gói cước!')
            return
        }

        try {
            setIsLoading(true)
            const newListAddPackages = listAddPackages?.map((pack: any) => {
                if (pack.dataPack === dataModalProvider.dataPack) {
                    // Replace all providers with current providerMetadata (valid ones only)
                    return { ...pack, providers: validProviders }
                }
                return pack
            })
            setListAddPackages(newListAddPackages)
            toastSuccess('Đã cập nhật nhà cung cấp cho gói cước!')
            setProviderMetadata([{...DEFAULT_PROVIDER_METADATA}])
            setDataModalProvider(null)
        } catch (error) {
            toastError('Vui lòng kiểm tra lại thông tin!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleAddPackage = (values: any) => {
        try {
            setIsLoading(true)
            const { name, dataPack, description, priceUnit, scope, type, source } = values
            if (source === TDM_SOURCE && !dataPack.includes('#')) {
                toastError('Mã gói cước không hợp lệ!')
                return
            }

            // Create package without providers, providers will be added via modal
            const newPackage = {
                name,
                dataPack,
                source,
                priceUnit,
                description,
                scope,
                type,
                providers: []
            }

            setListAddPackages([...listAddPackages, newPackage])
            formCreate.resetFields()
            toastSuccess('Đã thêm gói cước vào danh sách!')
        } catch (error) {
            toastError('Vui lòng kiểm tra lại thông tin!')
        } finally {
            setIsLoading(false)
        }
    }

    const sponsorColumns: TableProps<any>['columns'] = [
        {
            key: 'id',
            title: 'ID',
            render: (_text, _record, index) => index + 1,
        },
        {
            key: 'name',
            title: "Tên gói cước",
            dataIndex: 'name',
        },
        {
            key: 'dataPack',
            title: "Mã gói cước",
            dataIndex: 'dataPack',
        },
        {
            key: 'source',
            title: "Nguồn",
            dataIndex: 'source',
        },
        {
            key: 'priceUnit',
            title: "Giá gốc",
            dataIndex: 'priceUnit',
            render: (priceUnit) => formatCurrency(priceUnit),
        },
        {
            key: 'scope',
            title: "Phạm vi",
            dataIndex: 'scope',
        },
        {
            key: 'type',
            title: "Loại",
            dataIndex: 'type',
        },
        {
            key: 'description',
            title: "Mô tả",
            dataIndex: 'description',
        },
        {
            key: 'action',
            title: "Hành động",
            render: (_text, record, index) => (
                <Space>
                    <Button
                        onClick={() => {
                            setDataModalProvider(record)
                            // Load existing providers or create empty one
                            if (record.providers && record.providers.length > 0) {
                                setProviderMetadata(record.providers)
                            } else {
                                setProviderMetadata([{...DEFAULT_PROVIDER_METADATA, price: record.priceUnit, netPrice: record.priceUnit }])
                            }
                        }}
                        type="primary"
                    >
                        Quản lý NCC
                    </Button>
                    <Popconfirm
                        title="Xoá gói cước"
                        description="Xoá gói cước khỏi danh sách?"
                        onConfirm={() => {
                            const newList = listAddPackages.filter((_: any, i: any) => i !== index)
                            setListAddPackages(newList)
                        }}
                        okText="Xác nhận"
                        cancelText="Huỷ bỏ"
                    >
                        <Button type="primary" danger>Xoá</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ]

    const handleAddPackageDataSponsors = async () => {
        if (!otpConfirm) {
            toastError('Vui lòng nhập mã OTP xác nhận!')
            return
        }

        try {
            setIsLoading(true)

            // Flatten packages to create individual API requests for each price group
            const listPackagesReq: any[] = []
            const listMetaDataPackagesReq: any[] = []

            listAddPackages.forEach((pkg: any) => {
                listPackagesReq.push({
                    name: pkg.name,
                    dataPack: pkg.dataPack,
                    description: pkg.description,
                    priceUnit: pkg.priceUnit,
                    scope: pkg.scope,
                    type: pkg.type,
                    source: pkg.source
                })

                pkg.providers.forEach((group: ProviderMetadata) => {
                    listMetaDataPackagesReq.push({
                        code: pkg.dataPack,
                        original_price: group.price.toString(),
                        source: pkg.source,
                        discount_amount: group.discountAmount.toString(),
                        net_price: group.netPrice.toString(),
                        group_code: group.groupCode,
                        provider: group.provider,
                        service_code: group.serviceCode
                    })
                })
            })

            // Call apiAddPackageDataSponsors first
            const resp = await apiAddPackageDataSponsors({
                packages: listPackagesReq,
                otpConfirm: otpConfirm
            })

            if (resp.status !== 200) {
                toastError('Thêm gói cước thất bại!')
                return
            }

            // Call apiAddMetaDataPackageDataSponsor for each record individually
            let allMetaDataSuccess = true
            if (listMetaDataPackagesReq.length > 0) {
                for (const metadata of listMetaDataPackagesReq) {
                    try {
                        const respMetaData = await apiAddMetaDataPackageDataSponsor({
                            metadataPackage: metadata,
                            otpConfirm: otpConfirm
                        })
                        if (respMetaData.status !== 200) {
                            allMetaDataSuccess = false
                            console.error('Failed to add metadata:', metadata)
                        }
                    } catch (error) {
                        allMetaDataSuccess = false
                        console.error('Error adding metadata:', metadata, error)
                    }
                }
            }

            if (allMetaDataSuccess) {
                toastSuccess('Thêm gói cước thành công!')
                setListAddPackages([])
                setIsOpenModal(false)
            } else {
                toastError('Thêm gói cước thành công nhưng một số metadata thất bại!')
            }
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 200, marginBottom: 20 }}>
                <Button
                    onClick={() => navigate(PACKAGES + `?previousTab=dataSponsor`)}
                    icon={<LeftCircleOutlined />}
                    style={{ background: 'gray', color: 'white' }}
                >
                    Trở lại
                </Button>
            </div>
            <Form
                style={{ maxWidth: 900, width: '90%' }}
                form={formCreate}
                onFinish={handleAddPackage}
                autoComplete='off'
                labelCol={{ span: 4 }}
                labelAlign="left"
            >
                <Form.Item
                    label='Tên gói cước'
                    name='name'
                    rules={[{ required: true, message: 'Vui lòng nhập tên gói cước' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label='Mã gói cước'
                    name='dataPack'
                    rules={[{ required: true, message: 'Vui lòng nhập mã gói cước' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                  label='Nguồn'
                  name='source'
                  rules={[{ required: true, message: 'Vui lòng chọn nguồn' }]}
                >
                    <Select
                      placeholder='Chọn nguồn'
                      options={partnerPackageDataSponsorSourceOptions}
                    />
                </Form.Item>
                <Form.Item
                  label='Giá gốc'
                  name='priceUnit'
                  rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                >
                    <InputNumber
                        formatter={value => formatCurrency(Number(value))}
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                <Form.Item
                    label='Phạm vi'
                    name='scope'
                    rules={[{ required: true, message: 'Vui lòng nhập phạm vi' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    label='Loại'
                    name='type'
                    rules={[{ required: true, message: 'Vui lòng nhập loại' }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    label='Mô tả'
                    name='description'
                    rules={[{ required: true, message: 'Vui lòng nhập mô tả' }]}
                >
                    <Input.TextArea />
                </Form.Item>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Space>
                        <Button htmlType="submit" type="primary">Thêm</Button>
                        <Button htmlType="reset" type="dashed" danger>Clear</Button>
                    </Space>
                </div>
            </Form>
            <div style={{ marginTop: 20, overflow: 'auto' }}>
                <div style={{ maxHeight: 360, overflow: 'auto' }}>
                    <Table
                        rowKey={(_, index: any) => index}
                        style={{ overflow: 'auto' }}
                        columns={sponsorColumns}
                        dataSource={listAddPackages}
                        pagination={false}
                        bordered
                        footer={() => (
                            <div>Tổng {listAddPackages?.length || '0'} gói cước</div>
                        )}
                    />
                </div>
                <div style={{ textAlign: 'end', marginTop: 20 }}>
                    <Button
                        disabled={listAddPackages.length === 0}
                        onClick={() => setIsOpenModal(true)}
                        type="primary">Xác nhận</Button>
                </div>
            </div>
            <Modal title='Thêm gói cước' open={isOpenModal}
                onCancel={() => setIsOpenModal(false)}
                footer={[
                    <Button onClick={handleAddPackageDataSponsors} type='primary'>
                        Thêm
                    </Button>,
                ]}>
                <p><span style={{ color: 'red' }}>*</span> Mã OTP xác nhận</p>
                <Input value={otpConfirm} onChange={e => setOtpConfirm(e.target.value)} />
            </Modal>

            <Modal
                className='custom-modal-width'
                title={`Thêm nhà cung cấp cho gói cước ${dataModalProvider?.name}`}
                open={dataModalProvider !== null}
                centered
                onCancel={() => {
                    setProviderMetadata([{...DEFAULT_PROVIDER_METADATA}])
                    setDataModalProvider(null)
                }}
                footer={[
                    <Button key="submit" onClick={handleAddProviderMetadata} type="primary">
                        Thêm
                    </Button>
                ]}>
                <div style={{ border: '1px solid #d9d9d9', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', marginBottom: '16px' }}>
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={handleAddProviderField}
                            disabled={providerMetadata.length >= providerSourceOptions.length}
                        >
                            Thêm nhà cung cấp
                        </Button>
                    </div>

                    <div style={{ maxHeight: '530px', overflowY: 'auto', paddingRight: '8px' }}>
                        {providerMetadata.map((group, index) => (
                        <div key={index} style={{
                            border: '1px dashed #d9d9d9',
                            padding: '16px',
                            marginBottom: '16px',
                            borderRadius: '4px',
                            position: 'relative'
                        }}>
                            <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Nhà cung cấp {index + 1}</div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ marginBottom: '4px' }}>Nhà cung cấp <span style={{ color: 'red' }}>*</span></div>
                                <Select
                                    value={group.provider}
                                    onChange={(value) => handleProviderChange(index, 'provider', value || '')}
                                    placeholder='Chọn nhà cung cấp'
                                    options={providerSourceOptions}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ marginBottom: '4px' }}>Dịch vụ <span style={{ color: 'red' }}>*</span></div>
                                <Select
                                  value={group.serviceCode}
                                  onChange={(value) => handleProviderChange(index, 'serviceCode', value || '')}
                                  placeholder='Chọn dịch vụ'
                                  options={serviceCodeOptions}
                                  style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ marginBottom: '4px' }}>Giá gốc <span style={{ color: 'red' }}>*</span></div>
                                <InputNumber
                                    formatter={value => formatCurrency(Number(value))}
                                    value={group.price}
                                    onChange={(value) => handleProviderChange(index, 'price', value || 0)}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ marginBottom: '4px' }}>Giá chiết khấu <span style={{ color: 'red' }}>*</span></div>
                                <InputNumber
                                    formatter={value => formatCurrency(Number(value))}
                                    value={group.discountAmount}
                                    onChange={(value) => handleProviderChange(index, 'discountAmount', value || 0)}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ marginBottom: '4px' }}>Giá bán <span style={{ color: 'red' }}>*</span></div>
                                <InputNumber
                                    formatter={value => formatCurrency(Number(value))}
                                    value={group.netPrice}
                                    disabled
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ marginBottom: '4px' }}>Nhóm <span style={{ color: 'red' }}>*</span></div>
                                <Select
                                  value={group.groupCode}
                                  onChange={(value) => handleProviderChange(index, 'groupCode', value || '')}
                                  placeholder='Chọn nhóm'
                                  options={groupCodeOptions}
                                  style={{ width: '100%' }}
                                />
                            </div>

                            {providerMetadata.length > 1 && (
                                <Button
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() => handleRemoveProviderField(index)}
                                    style={{ position: 'absolute', top: '8px', right: '8px' }}
                                >
                                    Xóa
                                </Button>
                            )}
                        </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </div>
    )
}
